import { lazy, Suspense, useEffect, useState } from 'react'
import { HeadContent, Outlet, Scripts, createRootRouteWithContext } from '@tanstack/react-router'
import type { RouterContext } from '@/router'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import CssBaseline from '@mui/material/CssBaseline'
import { AnalyticsContextProvider } from '@/lib/analytics'

import Header from '@/components/Header'
import SiteFooter from '@/components/SiteFooter'
import CommandPalette from '@/components/CommandPalette'
import { SSOFooter } from '@/components/SSOFooter'
import { AppErrorBoundary } from '@/components/AppErrorBoundary'
import { AppThemeProvider } from '@/components/AppThemeProvider'
import { PageColorProvider } from '@/contexts/PageColorContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { LayoutProvider } from '@/contexts/LayoutContext'
import { initClientLogging } from '@/lib/clientLogger'
import { useJourneyDemo } from '@/lib/journeyDemo'
import { setSiteHeaderHeight } from '@/lib/siteHeader'

const NotFoundPage = lazy(() => import('@/components/NotFound'))
const TanStackRouterDevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-router-devtools').then(m => ({ default: m.TanStackRouterDevtoolsPanel })))
  : () => null;
const TanStackDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-devtools').then(m => ({ default: m.TanStackDevtools })))
  : () => null;

import appCss from '@/styles.css?url'

export const Route = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: () => (
    <Suspense
      fallback={
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem',
          textAlign: 'center',
          background: 'radial-gradient(ellipse at center, #1e293b 0%, #0f172a 100%)',
          gap: '1.5rem',
        }}>
          <h1 style={{ fontSize: '6rem', margin: 0, color: '#e2e8f0', fontWeight: 900, opacity: 0.2 }}>404</h1>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(226, 232, 240, 0.15)',
            borderTopColor: '#818cf8',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem', letterSpacing: '0.05em' }}>
            Loading scene...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      }
    >
      <NotFoundPage />
    </Suspense>
  ),
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Christian Battaglia — Product Engineer, Builder, Creator',
      },
      {
        name: 'description',
        content: 'Product engineer building open source tools and exploring music technology. Writing about infrastructure, frameworks, and creative technical solutions.',
      },
      // Open Graph
      {
        property: 'og:site_name',
        content: 'Christian Battaglia',
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:locale',
        content: 'en_US',
      },
      // Twitter Card (base — child routes add twitter:title/description/image)
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:creator',
        content: '@cdbattags',
      },
      {
        name: 'twitter:site',
        content: '@cdbattags',
      },
      // Author
      {
        name: 'author',
        content: 'Christian Battaglia',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      // Favicons — SVG preferred (supports dark/light mode), .ico fallback
      {
        rel: 'icon',
        type: 'image/svg+xml',
        href: '/favicon.svg',
      },
      {
        rel: 'icon',
        type: 'image/x-icon',
        href: '/favicon.ico',
        sizes: '32x32',
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'describedby',
        href: '/llms.txt',
        type: 'text/markdown',
        title: 'LLM information',
      },
      {
        rel: 'alternate',
        type: 'text/markdown',
        href: '/llms.txt',
        title: 'LLM information',
      },
      {
        rel: 'alternate',
        type: 'text/markdown',
        href: '/llms-full.txt',
        title: 'Product Engineering full text for LLMs',
      },
      // Fonts
      {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Kalam:wght@300;400;700&display=swap',
      },
    ],
    // Organization & Website structured data (for LLMO)
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Christian Battaglia',
          url: 'https://cdbattaglia.com',
          description: 'Personal website of Christian Battaglia featuring music technology, developer tools, and technical writing.',
          author: {
            '@type': 'Person',
            '@id': 'https://cdbattaglia.com/#person',
            name: 'Christian Battaglia',
            alternateName: 'Chris Battaglia',
            jobTitle: 'Chief Technology Officer',
            worksFor: {
              '@type': 'Organization',
              name: 'Slabstack',
              url: 'https://slabstack.com',
            },
            url: 'https://cdbattaglia.com',
            sameAs: [
              'https://github.com/cdbattags',
              'https://www.linkedin.com/in/cdbattags/',
              'https://twitter.com/cdbattags',
              'https://unsplash.com/@cdbattags',
              'https://open.spotify.com/user/cdbattaglia',
            ],
            knowsAbout: [
              'Product Engineering',
              'Engineering Leadership',
              'Organizational Design',
              'JavaScript',
              'TypeScript',
              'React',
              'Node.js',
              'Python',
              'Rust',
              'Infrastructure as Code',
              'Music Technology',
              'Audio Analysis',
            ],
          },
        }),
      },
    ],
  }),

  shellComponent: RootComponent,
})

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

// Provider composition utility
type ProviderConfig = [React.ComponentType<any>, Record<string, any>?];

function ComposeProviders({
  providers,
  children,
}: {
  providers: ProviderConfig[];
  children: React.ReactNode;
}) {
  return providers.reduceRight(
    (acc, [Provider, props = {}]) => <Provider {...props}>{acc}</Provider>,
    children
  );
}

function Providers({ children }: { children: React.ReactNode }) {
  const emotionCache = createCache({ key: 'css' });

  const providers: ProviderConfig[] = [
    [CacheProvider, { value: emotionCache }],
    [AppThemeProvider],
    [PageColorProvider],
    [LayoutProvider],
    [AuthProvider],
  ];

  return (
    <AnalyticsContextProvider>
      <ComposeProviders providers={providers}>
        <CssBaseline />
        {children}
      </ComposeProviders>
    </AnalyticsContextProvider>
  );
}

function SiteChrome({
  children,
  devtoolsMounted,
}: {
  children: React.ReactNode
  devtoolsMounted: boolean
}) {
  const demo = useJourneyDemo();

  useEffect(() => {
    document.documentElement.classList.toggle('journey-demo', demo);
    if (demo) setSiteHeaderHeight(0);
    return () => document.documentElement.classList.remove('journey-demo');
  }, [demo]);

  return (
    <>
      {!demo && <Header />}
      {!demo && <CommandPalette />}
      {children}
      {!demo && <SiteFooter />}
      {!demo && <SSOFooter />}
      {!demo && devtoolsMounted && (
        <Suspense>
          <TanStackDevtools
            config={{
              position: 'bottom-right',
            }}
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        </Suspense>
      )}
    </>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const [devtoolsMounted, setDevtoolsMounted] = useState(false);
  const demo = useJourneyDemo();

  useEffect(() => {
    initClientLogging();
    if (import.meta.env.DEV) setDevtoolsMounted(true);
  }, []);

  return (
    <html
      lang="en"
      className={demo ? 'journey-demo' : undefined}
      suppressHydrationWarning
    >
      <head>
        <HeadContent />
      </head>
      <body
        suppressHydrationWarning
        data-new-gr-c-s-check-loaded=""
        data-gr-ext-installed=""
      >
        <AppErrorBoundary>
          <Providers>
            <SiteChrome devtoolsMounted={devtoolsMounted}>
              {children}
            </SiteChrome>
          </Providers>
        </AppErrorBoundary>
        <Scripts />
      </body>
    </html>
  )
}
